import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';
import { VechicleService } from 'app/modules/components/transport/vechicle/vehicle.service';
import { Vehicle } from 'app/modules/components/modals/dto/routes-detail-by-businesss-partnert-vehicle-dto';
import {
  VehicleSelectorModalComponent,
  VehicleSelectorModalData,
  VehicleSelectorModalResult
} from 'app/shared/components/vehicle-selector-modal/vehicle-selector-modal.component';
import {
  VehicleRoutesModalComponent,
  VehicleRoutesModalData
} from 'app/shared/components/vehicle-routes-modal/vehicle-routes-modal.component';
import {
  VehicleTripsModalComponent,
  VehicleTripsModalData
} from 'app/shared/components/vehicle-trips-modal/vehicle-trips-modal.component';
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  CountButtonCellRendererComponent
} from 'app/shared/components/smart-grid';
import { VehicleNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/vehicle-nav-cell-renderer/vehicle-nav-cell-renderer.component';
import { BusinessPartnerNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/business-partner-nav-cell-renderer/business-partner-nav-cell-renderer.component';

@Component({
  selector: 'app-vehicles-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SmartGridComponent
  ],
  templateUrl: './vehicles-tab.component.html',
  styleUrl: './vehicles-tab.component.scss'
})
export class VehiclesTabComponent {
  private readonly dialog = inject(MatDialog);
  private readonly vehicleService = inject(VechicleService);

  /** Vehículos con info de trips/subroutes */
  protected readonly vehiclesInfo = signal<Vehicle[]>([]);
  protected readonly isLoading = signal<boolean>(false);

  /** Definición de columnas para SmartGrid */
  readonly columnDefs: ColDef<Vehicle>[] = [
    {
      field: 'name',
      headerName: 'Vehículo',
      width: 180,
      cellRenderer: VehicleNavCellRendererComponent
    },
    {
      field: 'type.name' as any,
      headerName: 'Tipo',
      width: 100,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'plate',
      headerName: 'Placa',
      width: 100,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'businessPartner.name' as any,
      headerName: 'Propietario',
      width: 150,
      cellRenderer: BusinessPartnerNavCellRendererComponent,
      cellRendererParams: { dataPath: 'businessPartner' }
    },
    {
      field: 'subRoutes' as any,
      headerName: 'Rutas',
      width: 80,
      cellRenderer: CountButtonCellRendererComponent,
      cellRendererParams: {
        countField: 'subRoutes',
        activeClass: 'bg-green-100 text-green-700',
        tooltip: 'Ver rutas asignadas',
        onClick: (vehicle: Vehicle) => this.openRoutesModal(vehicle)
      }
    },
    {
      field: 'currentTrips' as any,
      headerName: 'Viajes',
      width: 80,
      cellRenderer: CountButtonCellRendererComponent,
      cellRendererParams: {
        countField: 'currentTrips',
        activeClass: 'bg-blue-100 text-blue-700',
        tooltip: 'Ver viajes activos',
        onClick: (vehicle: Vehicle) => this.openTripsModal(vehicle)
      }
    }
  ];

  /** Configuración del SmartGrid */
  readonly gridConfig: GridConfig<Vehicle> = {
    rowIdField: '_id',
    selection: { enabled: false },
    pagination: { enabled: false },
    sorting: { enabled: false },
    search: { enabled: false },
    columnFilters: { enabled: false },
    styling: {
      maxHeight: '100%',
      stripedRows: true,
      density: 'comfortable'
    },
    emptyState: {
      message: 'No hay vehículos para mostrar',
      showIcon: true
    }
  };

  openVehicleSelector(): void {
    const dialogRef = this.dialog.open(VehicleSelectorModalComponent, {
      width: '95%',
      maxWidth: '800px',
      data: {
        selectedVehicles: this.vehiclesInfo() as unknown as VehicleAttributes[]
      } as VehicleSelectorModalData
    });

    dialogRef.afterClosed().subscribe((result: VehicleSelectorModalResult | undefined) => {
      if (!result) return; // Usuario canceló

      if (result.vehicles?.length) {
        this.loadVehiclesInfo(result.vehicles);
      } else {
        // Usuario quitó toda la selección
        this.vehiclesInfo.set([]);
      }
    });
  }

  private loadVehiclesInfo(vehicles: VehicleAttributes[]): void {
    const vehicleIds = vehicles.map(v => String(v._id));
    this.isLoading.set(true);

    this.vehicleService.getWithTripsInfo({}, vehicleIds).subscribe({
      next: (response) => {
        this.vehiclesInfo.set(response.data ?? []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  openRoutesModal(vehicle: Vehicle): void {
    if (!vehicle.subRoutes?.length) return;

    this.dialog.open(VehicleRoutesModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        subRoutes: vehicle.subRoutes,
        vehicleName: vehicle.name || vehicle.code || 'Sin nombre'
      } as VehicleRoutesModalData
    });
  }

  openTripsModal(vehicle: Vehicle): void {
    if (!vehicle.currentTrips?.length) return;

    this.dialog.open(VehicleTripsModalComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        trips: vehicle.currentTrips,
        vehicleName: vehicle.name || vehicle.code || 'Sin nombre'
      } as VehicleTripsModalData
    });
  }
}
